/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import { breakpoints } from '@coursera/cds-core';

import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';

type Props = {
  children: React.ReactNode;
  anchorId?: string;
  spacing?: number;
  dataTestId?: string;
};

const styles = {
  section: (spacing: number, mobileSpacing: number, fixedPageNavHeight?: number) => css`
    margin: ${spacing}px 0;
    scroll-margin-top: ${fixedPageNavHeight ? fixedPageNavHeight + 24 : 132}px;

    ${breakpoints.down('sm')} {
      margin: ${mobileSpacing}px 0;
    }
  `,
};

const Section = React.forwardRef(
  ({ children, anchorId, spacing, dataTestId }: Props, ref?: React.Ref<HTMLDivElement>) => {
    const sectionRef = useRef<HTMLDivElement>(null);

    let updatedSpacing = spacing;
    let updatedMobileSpacing = spacing;
    if (spacing === undefined) {
      updatedSpacing = 48;
      updatedMobileSpacing = 48;
    }

    const { addSectionElementRef, fixedPageNavHeight } = usePageNavigation();

    useEffect(() => {
      if (sectionRef.current && addSectionElementRef && anchorId) {
        addSectionElementRef(anchorId, sectionRef.current);
      }
    }, [addSectionElementRef, anchorId]);

    return (
      <div
        css={styles.section(updatedSpacing as number, updatedMobileSpacing as number, fixedPageNavHeight)}
        id={anchorId}
        data-testid={dataTestId}
        ref={sectionRef}
      >
        <div ref={ref}>{children}</div>
      </div>
    );
  }
);

export default Section;
